import type { JSX } from 'react'

/**
 * A deployment's state, readable without colour.
 *
 * The criterion is "state encoded in form, not colour alone", and the reason is
 * practical rather than decorative: this list is read by people with colour
 * vision deficiency, on a projector in a meeting, and pasted as a screenshot
 * into a ticket. So each status carries its own word *and* its own shape, and
 * colour is the third signal rather than the only one.
 *
 * The words are the API's, unchanged. Translating `apply-failed` into something
 * friendlier here would mean two vocabularies for the same fact — the one in the
 * console and the one in a log — and someone reconciling them under pressure.
 */

const SHAPES: Readonly<Record<string, { readonly glyph: string; readonly label: string }>> = {
  live: { glyph: '●', label: 'Live' },
  'in-progress': { glyph: '◐', label: 'In progress' },
  'apply-failed': { glyph: '▲', label: 'Apply failed' },
  // The outline of the failure shape (IM-185): applied, but not confirmed. A
  // shape of its own, so it reads without colour beside `apply-failed`.
  unverified: { glyph: '△', label: 'Unverified' },
  destroyed: { glyph: '○', label: 'Destroyed' },
  'never-applied': { glyph: '◌', label: 'Never applied' },
}

export const DeploymentStatus = ({ status }: { status: string }): JSX.Element => {
  const shape = SHAPES[status]
  return (
    <span className={`status status-${status}`}>
      {/* Decorative: the word beside it is the accessible name, so a screen
          reader is not made to announce a glyph. */}
      <span aria-hidden="true">{shape?.glyph ?? '◌'}</span>{' '}
      {/* An unknown status shows its raw value rather than a friendly guess: a
          console that silently renders a state it does not know as "Unknown" is
          how a new state goes unnoticed for a release. */}
      {shape?.label ?? status}
    </span>
  )
}

/**
 * A run's state, word and shape, like a deployment's.
 *
 * The three approval states (IM-102, IM-39) are why this carries a glyph now:
 * `awaiting-approval` beside `requested` in a history column is the difference
 * between "nothing will happen until somebody agrees" and "it is about to
 * start", and a colour alone would be the only thing saying so.
 */
const RUN_STATES: Readonly<Record<string, { readonly glyph: string; readonly label: string }>> = {
  'awaiting-approval': { glyph: '⏸', label: 'Awaiting approval' },
  requested: { glyph: '◌', label: 'Requested' },
  running: { glyph: '◐', label: 'Running' },
  succeeded: { glyph: '✓', label: 'Succeeded' },
  failed: { glyph: '✕', label: 'Failed' },
  rejected: { glyph: '⊘', label: 'Rejected' },
  expired: { glyph: '◷', label: 'Expired' },
}

export const RunState = ({ state }: { state: string }): JSX.Element => {
  const shape = RUN_STATES[state]
  return (
    <span className={`run-state run-state-${state}`}>
      {shape === undefined ? null : <span aria-hidden="true">{shape.glyph}</span>}
      {shape === undefined ? null : ' '}
      {shape?.label ?? state}
    </span>
  )
}
